import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import {
  CreateResourceData,
  GetResourceEntriesList,
  GetResourceList,
  UpdateResourceData,
} from 'api/resource';
import {
  Resource,
  ResourceInventoryEntry,
} from 'constants/interface';
import { RootState } from 'state';

const resourceEntityAdapter = createEntityAdapter<Resource>();
const entriesEntityAdapter = createEntityAdapter<ResourceInventoryEntry>();

export interface ResourceEntriesState extends EntityState<ResourceInventoryEntry> {
  isLoading: boolean
  remoteTotal: number
}

export interface ResourcesState extends EntityState<Resource> {
  isLoading: boolean
  remoteTotal: number
  entries: ResourceEntriesState
}

export const initialState: ResourcesState = resourceEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
  entries: entriesEntityAdapter.getInitialState({
    isLoading: false,
    remoteTotal: 0,
  }),
});

export const createResourceAction = createAsyncThunk(
  'resources/create',
  async (payload: CreateResourceData) => {
    const resourceData = await apiClient.resource.createResource(payload);
    return resourceData;
  },
);

export const updateResourceAction = createAsyncThunk(
  'resources/update',
  async (payload: UpdateResourceData) => {
    const resourceData = await apiClient.resource.updateResource(payload);
    return resourceData;
  },
);

export const getResourceAction = createAsyncThunk(
  'resources/get',
  async (payload: {
    resourceId: string,
  }) => {
    const { resourceId } = payload;
    const response = await apiClient.resource.getResource(resourceId);

    return response;
  },
);

export const getResourceListAction = createAsyncThunk(
  'resources/list',
  async (payload: GetResourceList) => {
    const response = await apiClient
      .resource
      .getResourceList(payload);

    return response;
  },
);

// Entries
export const getResourceEntriesListAction = createAsyncThunk(
  'resources/entries/list',
  async (payload: GetResourceEntriesList) => {
    const response = await apiClient
      .resource
      .getResourceEntriesList(payload);

    return response;
  },
);

export const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    cleanResourcesAction: resourceEntityAdapter.removeAll,
    cleanResourceEntriesAction: (state) => {
      entriesEntityAdapter.removeAll(state.entries);
    },
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getResourceListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getResourceListAction.fulfilled, (state, action) => {
      resourceEntityAdapter.removeAll(state);
      resourceEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getResourceListAction.rejected, (state) => {
      state.isLoading = false;
    });

    // Entries
    builder.addCase(getResourceEntriesListAction.pending, (state) => {
      state.entries.isLoading = true;
    });
    builder.addCase(getResourceEntriesListAction.fulfilled, (state, action) => {
      entriesEntityAdapter.removeAll(state.entries);
      entriesEntityAdapter.addMany(state.entries, action.payload.data);
      state.entries.remoteTotal = action.payload.pagination.total;
      state.entries.isLoading = false;
    });
    builder.addCase(getResourceEntriesListAction.rejected, (state) => {
      state.entries.isLoading = false;
    });
  },
});

export const {
  cleanResourcesAction,
  cleanResourceEntriesAction,
} = resourcesSlice.actions;

// Selectors

export const isLoadingResourcesSelector = (state: RootState):
  boolean => state.resources.isLoading;

export const resourcesEntitiesSelector = (state: RootState):
  Dictionary<Resource> => state.resources.entities;

export const resourcesIdsSelector = (state: RootState):
  EntityId[] => state.resources.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.resources.remoteTotal;

// Entries selectors

export const isLoadingResourceEntriesSelector = (state: RootState):
  boolean => state.resources.entries.isLoading;

export const resourceEntriesEntitiesSelector = (state: RootState):
  Dictionary<ResourceInventoryEntry> => state.resources.entries.entities;

export const resourceEntriesIdsSelector = (state: RootState):
  EntityId[] => state.resources.entries.ids;

export const resourceEntriesRemoteTotalSelector = (state: RootState):
  number => state.resources.entries.remoteTotal;
